import { useEffect, useState, useMemo, useCallback } from "preact/hooks";
import type { RoutableProps } from "preact-router";
import type { ExploreSample, ReputationVerdict } from "@sage/shared";
import { getClerkAuthToken } from "../../auth/clerk";
import { fetchExploreSamples } from "../../lib/explore-client";
import { formatSize, truncateSha256, formatDate } from "./samples";
import { Navbar } from "../../components/layout/Navbar";

const MOCK_SAMPLES: ExploreSample[] = [
  {
    id: "mock-001",
    sha256: "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
    fileName: "malware_sample_01.exe",
    fileType: "PE32+ executable (GUI) x86-64",
    fileSize: 245760,
    verdict: "malicious" as ReputationVerdict,
    tags: ["trojan", "downloader"],
    firstSeen: "2026-06-10T08:15:00Z",
    lastScanned: "2026-06-17T14:30:00Z",
    createdAt: "2026-06-10T08:15:00Z",
  },
  {
    id: "mock-002",
    sha256: "a7ffc6f8bf1ed76651c14756a061d662f580ff4de43b49fa82d80a4b80f8434a",
    fileName: "invoice_report.pdf",
    fileType: "PDF document",
    fileSize: 1048576,
    verdict: "clean" as ReputationVerdict,
    tags: [],
    firstSeen: "2026-06-12T10:00:00Z",
    lastScanned: "2026-06-17T09:00:00Z",
    createdAt: "2026-06-12T10:00:00Z",
  },
  {
    id: "mock-003",
    sha256: "6b23c0d5f35d1b11f9b683f0b1a617355deb11277d91ae091d399c655b87940d",
    fileName: "budget_q2.docm",
    fileType: "Microsoft Word Document",
    fileSize: 512000,
    verdict: "suspicious" as ReputationVerdict,
    tags: ["macro", "obfuscated"],
    firstSeen: "2026-06-14T16:45:00Z",
    lastScanned: "2026-06-16T22:15:00Z",
    createdAt: "2026-06-14T16:45:00Z",
  },
  {
    id: "mock-004",
    sha256: "d7a8fbb307d7809469ca9abcb0082e4f8d5651e46d3cdb762d02d0bf37c9e592",
    fileName: "exploit_kit.js",
    fileType: "JavaScript",
    fileSize: 20480,
    verdict: "critical" as ReputationVerdict,
    tags: ["exploit", "cve-2024-1234"],
    firstSeen: "2026-06-15T03:20:00Z",
    lastScanned: "2026-06-18T01:00:00Z",
    createdAt: "2026-06-15T03:20:00Z",
  },
  {
    id: "mock-005",
    sha256: "2cf24dba5fb0a30e26e83b2ac5b9e29e1b161e5c1fa7425e73043362938b9824",
    fileName: "helper.dll",
    fileType: "DLL x86",
    fileSize: 1572864,
    verdict: "unknown" as ReputationVerdict,
    tags: [],
    firstSeen: "2026-06-08T12:00:00Z",
    lastScanned: "2026-06-17T18:30:00Z",
    createdAt: "2026-06-08T12:00:00Z",
  },
];

type SortKey = "id" | "fileSize" | "firstSeen" | "lastScanned";
type SortDir = "asc" | "desc";

type PageStatus =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready" };

export function ExplorePage(_props: RoutableProps) {
  const [status, setStatus] = useState<PageStatus>({ kind: "loading" });
  const [samples, setSamples] = useState<ExploreSample[]>([]);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("id");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [scanCount, setScanCount] = useState(0);

  useEffect(() => {
    document.title = "Sage | Explore";
  }, []);

  // Fetch samples from the explore API
  useEffect(() => {
    async function load() {
      try {
        setStatus({ kind: "loading" });
        const token = await getClerkAuthToken();
        const result = await fetchExploreSamples(token, {
          limit: 100,
          sort: sortKey === "fileSize" ? "file_size" : sortKey,
          order: sortDir,
        });

        if (!result.ok) {
          // Fall back to mock data for POC/testing
          setSamples(MOCK_SAMPLES);
          setStatus({ kind: "ready" });
          return;
        }

        setSamples(result.data.samples);
        setStatus({ kind: "ready" });
      } catch {
        // Defensive: if anything throws, fall back to mock data
        setSamples(MOCK_SAMPLES);
        setStatus({ kind: "ready" });
      }
    }

    load();
  }, [sortKey, sortDir]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return samples;
    return samples.filter(
      (s) =>
        s.sha256.toLowerCase().includes(q) ||
        (s.fileType ?? "").toLowerCase().includes(q) ||
        s.id.toLowerCase().includes(q)
    );
  }, [samples, filter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === "firstSeen" || sortKey === "lastScanned") {
        const aVal = a[sortKey] ? new Date(a[sortKey]!).getTime() : 0;
        const bVal = b[sortKey] ? new Date(b[sortKey]!).getTime() : 0;
        return (aVal - bVal) * dir;
      }
      if (sortKey === "fileSize") {
        return ((a.fileSize ?? 0) - (b.fileSize ?? 0)) * dir;
      }
      // id sort
      return a.id.localeCompare(b.id) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function getSortAria(col: SortKey): "ascending" | "descending" | "none" {
    if (sortKey !== col) return "none";
    return sortDir === "asc" ? "ascending" : "descending";
  }

  /** Mock re-scan — deducts from a demo allowance. Replace with real API call. */
  function handleRescan(sample: ExploreSample, e: MouseEvent) {
    e.stopPropagation();
    const next = scanCount + 1;
    if (next > 25) {
      setToast("Re-scan limit reached (25/25 used). Upgrade your plan for more.");
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setScanCount(next);
    setToast(`Re-scan queued for ${truncateSha256(sample.sha256)} (${next}/25 used, ${25 - next} remaining)`);
    setTimeout(() => setToast(null), 3000);
  }

  function handleRowKeyDown(e: KeyboardEvent, id: string) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedId((prev) => (prev === id ? null : id));
    }
  }

  function renderSortIcon(col: SortKey) {
    if (sortKey !== col) return <span class="sort-icon sort-icon--idle" aria-hidden="true">▸</span>;
    return <span class="sort-icon" aria-hidden="true">{sortDir === "asc" ? "▲" : "▼"}</span>;
  }

  return (
    <div class="app-shell">
      <Navbar />

      <main class="explore-main">
        <div class="explore-header">
          <h1 class="explore-title">Explore samples</h1>
          <p class="explore-desc">
            Recently scanned samples across the platform. Click a row to inspect details.
          </p>
        </div>

        {status.kind === "loading" && samples.length === 0 ? (
          <div class="explore-loading" role="status" aria-live="polite">
            Loading sample catalog…
          </div>
        ) : status.kind === "error" ? (
          <div class="explore-error" role="alert">
            <p>{status.message}</p>
            {status.message.includes("Sign in") ? (
              <a class="primary-link" href="/login">Sign in to view samples</a>
            ) : null}
          </div>
        ) : (
          <>
            <label for="explore-filter" class="sr-only">Filter samples table</label>
            <input
              id="explore-filter"
              class="explore-filter"
              type="text"
              placeholder="Filter by ID, hash, or type..."
              value={filter}
              onInput={(e) => setFilter(e.currentTarget.value)}
            />

            {toast && (
              <div class="explore-toast" role="status" aria-live="polite">
                {toast}
              </div>
            )}

            <div class="explore-table-wrap">
              <table class="explore-table">
                <thead>
                  <tr>
                    <th class="col-id sortable" scope="col" aria-sort={getSortAria("id")} onClick={() => toggleSort("id")} onKeyDown={(e) => { if (e.key === "Enter") toggleSort("id"); }} tabIndex={0} role="columnheader">
                      ID {renderSortIcon("id")}
                    </th>
                    <th class="col-sha256" scope="col">SHA256</th>
                    <th class="col-size sortable" scope="col" aria-sort={getSortAria("fileSize")} onClick={() => toggleSort("fileSize")} onKeyDown={(e) => { if (e.key === "Enter") toggleSort("fileSize"); }} tabIndex={0} role="columnheader">
                      Size {renderSortIcon("fileSize")}
                    </th>
                    <th class="col-type" scope="col">Type</th>
                    <th class="col-verdict" scope="col">Detection</th>
                    <th class="col-first sortable" scope="col" aria-sort={getSortAria("firstSeen")} onClick={() => toggleSort("firstSeen")} onKeyDown={(e) => { if (e.key === "Enter") toggleSort("firstSeen"); }} tabIndex={0} role="columnheader">
                      First Seen {renderSortIcon("firstSeen")}
                    </th>
                    <th class="col-last sortable" scope="col" aria-sort={getSortAria("lastScanned")} onClick={() => toggleSort("lastScanned")} onKeyDown={(e) => { if (e.key === "Enter") toggleSort("lastScanned"); }} tabIndex={0} role="columnheader">
                      Last Scanned {renderSortIcon("lastScanned")}
                    </th>
                    <th class="col-action" scope="col">Re-scan</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colspan={8} class="explore-empty">
                        {filter ? "No samples match your filter." : "No samples yet. Run some hash lookups to populate the catalog."}
                      </td>
                    </tr>
                  ) : (
                    sorted.map((row) => (
                      <tr
                        key={row.id}
                        class={`explore-row${selectedId === row.id ? " explore-row--selected" : ""}`}
                        tabIndex={0}
                        role="row"
                        aria-selected={selectedId === row.id}
                        onClick={() => setSelectedId(selectedId === row.id ? null : row.id)}
                        onKeyDown={(e) => handleRowKeyDown(e, row.id)}
                      >
                        <td class="col-id">{row.id.slice(0, 8)}</td>
                        <td class="col-sha256 mono">{truncateSha256(row.sha256)}</td>
                        <td class="col-size">{row.fileSize !== null ? formatSize(row.fileSize) : "—"}</td>
                        <td class="col-type">{row.fileType ?? "unknown"}</td>
                        <td class="col-verdict">
                          <span class={`verdict-badge verdict-badge--${row.verdict}`}>
                            {row.verdict}
                          </span>
                        </td>
                        <td class="col-first">{row.firstSeen ? formatDate(row.firstSeen) : "—"}</td>
                        <td class="col-last">{row.lastScanned ? formatDate(row.lastScanned) : "—"}</td>
                        <td class="col-action">
                          <button
                            class="rescan-btn"
                            onClick={(e) => handleRescan(row, e)}
                            title="Queue re-scan (deducts from allowance)"
                          >
                            Re-scan
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

      </main>
    </div>
  );
}
